import {
  AdminSwitch,
  Box,
  CollapsibleComponent,
  Disclaimer,
  Div,
  ErrorToast,
  H3,
  H5,
  P,
  lightTheme,
} from '@vgrubs/components';
import {
  ChangeEventHandler,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  ImproveMenuAiDto,
  MenuAiDraftStatus,
  MenuAiImprovement,
  MenuAiSubPrompts,
} from '../../../types/api/menuAi.types';
import { ProgressBarBackground, ProgressBarFill } from '../MenyStyled';
import { PromptTextarea } from '../components/PromptTextarea';

const IMPROVEMENTS: {
  key: MenuAiImprovement;
  label: string;
  description: string;
  placeholder: string;
}[] = [
  {
    key: 'recategorize',
    label: 'Menu Structure',
    description:
      'Reorganize the whole menu: rename categories, regroup items, and set a sensible display order.',
    placeholder: 'e.g. group all drinks together',
  },
  {
    key: 'pricing',
    label: 'Item Pricing',
    description:
      'Adjust prices for both items and modifiers based on demand, competition, and other signals.',
    placeholder: 'e.g. price competitively for delivery',
  },
  {
    key: 'names',
    label: 'Item Name',
    description:
      'Improve item names using market trends and proven wording to boost appeal and search visibility (SEO).',
    placeholder: 'e.g. keep names short and searchable',
  },
  {
    key: 'descriptions',
    label: 'Item Description',
    description:
      'Write or improve item descriptions to be appetizing, accurate, and concise for online ordering.',
    placeholder: 'e.g. appetizing and concise',
  },
  {
    key: 'prepNotes',
    label: 'Item Preparation Notes',
    description:
      'Write or improve kitchen prep notes so portions, timing, and plating stay consistent. Adds them where missing.',
    placeholder: 'e.g. include portion sizes and timing',
  },
];

/**
 * One improvement toggle whose prompt field slides open/closed via `Collapsible`
 * (max-height animation), so it needs the content's measured height. A
 * ResizeObserver keeps that height in sync with the drag-resizable prompt field
 * so nothing gets clipped mid-animation.
 */
const ImprovementRow = ({
  imp,
  checked,
  onToggle,
  value,
  onChange,
}: {
  imp: (typeof IMPROVEMENTS)[number];
  checked: boolean;
  onToggle: () => void;
  value: string;
  onChange: ChangeEventHandler<HTMLTextAreaElement>;
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const measure = () => setHeight(el.clientHeight);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <Box>
      <Div $flex $between $alignCenter $gap="16px">
        <div style={{ flex: 1, minWidth: 0 }}>
          <P $bold $mb="4">
            {imp.label}
          </P>
          <Disclaimer $mb="0" $color={lightTheme.lightGray}>
            {imp.description}
          </Disclaimer>
        </div>
        <Div $flexNone>
          <AdminSwitch $gradient checked={checked} onChange={onToggle} />
        </Div>
      </Div>

      <CollapsibleComponent expanded={checked} elementHeight={height}>
        {/* paddingTop lives on the measured node so the gap animates with it;
            userSelect re-enables selection that CollapsibleComponent disables,
            so the prompt textarea stays selectable. */}
        <div
          ref={contentRef}
          style={{ paddingTop: '16px', userSelect: 'text' }}
        >
          <Div $flex $column $gap="12px">
            <PromptTextarea
              placeholder={imp.placeholder}
              description={imp.description}
              value={value}
              onChange={onChange}
              $textareaHeight="80"
              expandTitle={`${imp.label} prompt`}
            />
          </Div>
        </div>
      </CollapsibleComponent>
    </Box>
  );
};

/**
 * Imperative handle so the "Improve with AI" button — which now lives in the
 * modal footer (a separate subtree) — can trigger the request that's built from
 * this panel's form state.
 */
export type MenuAIControlsHandle = { submit: () => void };

export const MenuAIControlsPanel = forwardRef<
  MenuAIControlsHandle,
  {
    status: MenuAiDraftStatus | null;
    progress: number;
    onImprove: (dto: ImproveMenuAiDto) => void;
  }
>(({ status, progress, onImprove }, ref) => {
  const [selected, setSelected] = useState<Record<MenuAiImprovement, boolean>>({
    recategorize: false,
    pricing: false,
    names: false,
    descriptions: false,
    prepNotes: false,
    images: false,
  });
  const [prompts, setPrompts] = useState<
    Partial<Record<MenuAiImprovement, string>>
  >({});

  const handleImprove = () => {
    const improvements = IMPROVEMENTS.map((i) => i.key).filter(
      (k) => selected[k]
    );

    if (improvements.length === 0) {
      ErrorToast('Select at least one improvement.');
      return;
    }

    const subPrompts: MenuAiSubPrompts = {};
    improvements.forEach((k) => {
      const p = prompts[k]?.trim();
      if (p) subPrompts[k] = p;
    });

    onImprove({
      improvements,
      subPrompts: Object.keys(subPrompts).length ? subPrompts : undefined,
    });
  };

  useImperativeHandle(ref, () => ({ submit: handleImprove }));

  const busy = status === 'pending' || status === 'processing';

  if (busy) {
    return (
      <Div $flex $column $gap="16px" $width="100%">
        <H5 $m="0" $center>
          {status === 'processing'
            ? 'Saving your menu…'
            : 'Improving your menu…'}
        </H5>
        <ProgressBarBackground>
          <ProgressBarFill progress={progress} $gradient />
        </ProgressBarBackground>
        <Disclaimer $m="0" $center>
          {progress}%
        </Disclaimer>
        <P $center $color={lightTheme.lightGray} $m="0">
          This can take a few minutes. <br /> Please keep this window open.
        </P>
      </Div>
    );
  }

  return (
    <Div $flex $column $gap="8px">
      <div>
        <H3 $mb="4" $center>
          Improve menu with AI
        </H3>
        <P $mb="16" $center $color={lightTheme.lightGray}>
          Choose what to improve. <br /> Add a prompt to guide each change.
        </P>
      </div>

      {IMPROVEMENTS.map((imp) => (
        <ImprovementRow
          key={imp.key}
          imp={imp}
          checked={selected[imp.key]}
          onToggle={() =>
            setSelected((prev) => ({ ...prev, [imp.key]: !prev[imp.key] }))
          }
          value={prompts[imp.key] || ''}
          onChange={({ target }) =>
            setPrompts((prev) => ({ ...prev, [imp.key]: target.value }))
          }
        />
      ))}
    </Div>
  );
});

MenuAIControlsPanel.displayName = 'MenuAIControlsPanel';
